import { useCallback, useLayoutEffect, useRef, useState } from 'preact/hooks'
import { useRaf } from './use-ui-listeners'

/**
 * Hook to measure the natural height of an element for smooth height animations.
 *
 * The problem: CSS cannot animate `height: auto`. This hook measures the actual
 * rendered height of content so you can animate to a fixed pixel value.
 *
 * Uses a callback ref pattern for better compatibility with styled components
 * that may not forward refs properly.
 *
 * Usage:
 * ```tsx
 * function ExpandingPanel({ isOpen }) {
 *   const { setContentRef, measuredHeight } = useMeasuredHeight([isOpen])
 *
 *   return (
 *     <Container style={{ height: isOpen ? measuredHeight : 0 }}>
 *       <Content ref={setContentRef}>
 *         {children}
 *       </Content>
 *     </Container>
 *   )
 * }
 * ```
 *
 * @param deps - Optional dependency array that triggers remeasurement when values change
 * The content must always be rendered (can be visually hidden) for measurement to work.
 */
export function useMeasuredHeight<T extends HTMLElement = HTMLDivElement>(
  deps: unknown[] = [],
): {
  setContentRef: (el: T | null) => void
  measuredHeight: number
  remeasure: () => void
} {
  const elementRef = useRef<T | null>(null)
  const observerRef = useRef<ResizeObserver | null>(null)
  const [measuredHeight, setMeasuredHeight] = useState(0)

  const remeasure = useCallback(() => {
    if (elementRef.current) {
      // scrollHeight gives the full content height including overflow
      setMeasuredHeight(elementRef.current.scrollHeight)
    }
  }, [])

  // Callback ref - called when element is attached/detached
  const setContentRef = useCallback((el: T | null) => {
    // Cleanup old observer
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    elementRef.current = el

    if (el) {
      // Measure immediately
      setMeasuredHeight(el.scrollHeight)

      // Set up ResizeObserver for future changes
      if (typeof ResizeObserver !== 'undefined') {
        observerRef.current = new ResizeObserver(() => {
          if (elementRef.current) {
            setMeasuredHeight(elementRef.current.scrollHeight)
          }
        })
        observerRef.current.observe(el)
      }
    }
  }, [])

  // Cleanup observer on unmount
  useLayoutEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      elementRef.current = null
    }
  }, [])

  // Remeasure when deps change (e.g., when content visibility changes)
  // Use rAF to ensure layout has settled after CSS changes
  useRaf(() => {
    remeasure()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { setContentRef, measuredHeight, remeasure }
}

/**
 * Simpler version that just returns a measured height string with 'px' suffix.
 * Useful for direct use in styled-component props.
 */
export function useMeasuredHeightPx<T extends HTMLElement = HTMLDivElement>(
  deps: unknown[] = [],
): {
  setContentRef: (el: T | null) => void
  heightPx: string
  remeasure: () => void
} {
  const { setContentRef, measuredHeight, remeasure } = useMeasuredHeight<T>(deps)
  return {
    setContentRef,
    heightPx: `${measuredHeight}px`,
    remeasure,
  }
}
